import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { ProductCard } from "@/components/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";
import { useLikeToggle } from "@/hooks/useLikeToggle";
import { rememberBrowse } from "@/lib/browse";

const toList = (d) => (Array.isArray(d) ? d : d?.items || d?.listings || d?.wishlist || []);

export default function Wishlist() {
  usePageTitle("Saved items");
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const onToggleLike = useLikeToggle(setItems, { removeOnUnlike: true });

  useEffect(() => {
    if (!user) return;
    api.get("/wishlist")
      .then((r) => setItems(toList(r.data).map((x) => ({ ...(x.listing || x), is_liked: true })).map((x) => ({ ...x, likes_count: Math.max(1, x.likes_count || 0) }))))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [user]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((it) => `${it.title} ${it.seller_name || ""}`.toLowerCase().includes(q)) : items;
  }, [items, search]);
  useEffect(() => rememberBrowse(visible), [visible]);

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="wishlist-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar search={search} onSearch={setSearch} />
      <section className="px-4 sm:px-8 lg:px-12 pt-36 lg:pt-40 pb-24">
        <p className="eyebrow mb-3">Your wishlist</p>
        <div className="flex items-end justify-between gap-6 mb-12 lg:mb-16 border-b border-[#C5A059]/25 pb-6">
          <h1 className="font-display text-4xl sm:text-5xl text-[#0F0F0F]">Saved Items</h1>
          {!loading && <p data-testid="wishlist-count" className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A] shrink-0">{items.length} {items.length === 1 ? "piece" : "pieces"}</p>}
        </div>

        {error ? (
          <p data-testid="wishlist-error" className="text-sm text-red-700">Couldn't load your wishlist. Please refresh.</p>
        ) : loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 sm:gap-x-8 gap-y-8 sm:gap-y-12">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-none bg-[#F5EFE6]" />)}
          </div>
        ) : visible.length === 0 ? (
          <div data-testid="wishlist-empty" className="py-20 max-w-md">
            <p className="font-display text-2xl text-[#0F0F0F] mb-4">{search ? "No saved items match your search." : "Nothing saved yet."}</p>
            <p className="text-sm text-[#71717A] mb-8">Tap the heart on any piece to keep it here.</p>
            <Link to="/" data-testid="wishlist-browse-link" className="inline-block border border-[#0F0F0F] px-8 py-3 text-xs tracking-[0.3em] uppercase transition-colors hover:bg-[#0F0F0F] hover:text-[#C5A059]">
              Browse the edit
            </Link>
          </div>
        ) : (
          <div data-testid="wishlist-grid" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 sm:gap-x-8 gap-y-8 sm:gap-y-12">
            {visible.map((it, i) => <ProductCard key={it.id} item={it} index={i} onToggleLike={onToggleLike} />)}
          </div>
        )}
      </section>
    </div>
  );
}
